import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useParams, useSearchParams } from "react-router-dom";
import "../styles/ProjectDetailsScreen.scss";
import {
  getAuthHeaders,
  getEffectiveAccessLevel,
  getProfile,
  isAdmin,
  isModerator,
  isLeader,
  normalizeAccessLevel,
  normalizeProjectRole,
  parseRoles,
} from "../utils/auth";
import { apiFetch } from "../utils/api";
import { formatDeadline } from "../utils/date";
import {
  formatDisplayName,
  normalizeFullName,
  normalizeUsername,
} from "../utils/nameFormat";
import { recordTaskPageVisit } from "../utils/metrics";
import ProjectStackEditor, { normalizeProjectStack } from "../components/ProjectStackEditor";
import ProjectBannerImage from "../components/ProjectBannerImage";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8080";
const BASE_ROLES = ["Участник", "Разработчик", "Руководитель"];
const MAX_BANNER_BYTES = 3 * 1024 * 1024;
const BANNER_ACCEPT = "image/png,image/jpeg,image/webp,image/gif";

function parseMemberRole(role) {
  const parts = role
    .split(/[,;/|+]+/)
    .map((part) => part.trim())
    .filter(Boolean);
  let helper = false;
  const baseParts = parts.filter((part) => {
    const lower = part.toLowerCase();
    if (["хелпер", "helper"].includes(lower)) {
      helper = true;
      return false;
    }
    if (
      ["админ", "admin", "administrator", "модератор", "moderator", "владелец", "owner"].includes(
        lower
      )
    ) {
      return false;
    }
    return true;
  });
  const roles = parseRoles(baseParts.join(", "));
  let baseRole = "Участник";
  if (roles.includes("руководитель")) {
    baseRole = "Руководитель";
  } else if (roles.includes("разработчик")) {
    baseRole = "Разработчик";
  } else if (roles.includes("участник")) {
    baseRole = "Участник";
  } else if (baseParts[0]) {
    baseRole = baseParts[0];
  }
  return { baseRole, helper };
}

function buildRoleString(baseRole, helper) {
  const safeBase = (baseRole || "Участник").trim() || "Участник";
  const roles = [safeBase];
  if (helper) {
    roles.push("Хелпер");
  }
  return roles.join(", ");
}

export default function ProjectDetailsScreen() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [project, setProject] = useState(null);
  const [projectError, setProjectError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [tasksError, setTasksError] = useState("");
  const [isLoadingTasks, setIsLoadingTasks] = useState(true);
  const [isSavingTask, setIsSavingTask] = useState(false);
  const [taskFormError, setTaskFormError] = useState("");
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [completionNotes, setCompletionNotes] = useState({});
  const [usersDirectory, setUsersDirectory] = useState({});
  const [usersByFullName, setUsersByFullName] = useState({});
  const [users, setUsers] = useState([]);
  const [usersError, setUsersError] = useState("");
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [memberForm, setMemberForm] = useState({
    username: "",
    baseRole: "Участник",
    helper: false,
  });
  const [memberEdits, setMemberEdits] = useState({});
  const [membersActionError, setMembersActionError] = useState("");
  const [isMembersEditing, setIsMembersEditing] = useState(false);
  const [isStackEditing, setIsStackEditing] = useState(false);
  const [isStackSaving, setIsStackSaving] = useState(false);
  const [stackDraft, setStackDraft] = useState({});
  const [stackError, setStackError] = useState("");
  const [isBannerSaving, setIsBannerSaving] = useState(false);
  const [bannerError, setBannerError] = useState("");
  const profile = useMemo(() => getProfile(), []);
  const [effectiveAccessLevel, setEffectiveAccessLevel] = useState(
    getEffectiveAccessLevel(profile)
  );
  const [taskForm, setTaskForm] = useState({
    title: "",
    description: "",
    deadline: "",
    users: [],
    assignAll: false,
  });
  const [showCompletedTasks, setShowCompletedTasks] = useState(false);
  const fromProfile = searchParams.get("from") === "profile";
  const highlightedTaskId = useMemo(() => {
    const value = Number(searchParams.get("taskId"));
    return Number.isNaN(value) ? null : value;
  }, [searchParams]);
  const backPath = fromProfile ? "/profile" : "/projects";
  const taskListRef = useRef(null);

  const authHeaders = useMemo(() => getAuthHeaders(), []);

  useEffect(() => {
    recordTaskPageVisit("project_details");
  }, [id]);

  useEffect(() => {
    setIsMembersEditing(false);
    setIsStackEditing(false);
    setBannerError("");
  }, [id]);

  useEffect(() => {
    if (!isStackEditing) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !isStackSaving) {
        setStackDraft(normalizeProjectStack(project?.stack));
        setStackError("");
        setIsStackEditing(false);
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isStackEditing, isStackSaving, project]);

  useEffect(() => {
    if (!isMembersEditing) {
      setMembersActionError("");
    }
  }, [isMembersEditing]);

  useEffect(() => {
    let isActive = true;
    setIsLoading(true);
    setProjectError("");

    apiFetch(`${API_BASE}/projects/${id}`, {
      headers: authHeaders,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("failed");
        }
        return response.json();
      })
      .then((data) => {
        if (!isActive) {
          return;
        }
        setProject(data);
		setStackDraft(normalizeProjectStack(data?.stack));
      })
      .catch(() => {
        if (!isActive) {
          return;
        }
        setProjectError("Не удалось загрузить проект");
      })
      .finally(() => {
        if (!isActive) {
          return;
        }
        setIsLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [authHeaders, id]);

  const completedTasks = useMemo(
    () => tasks.filter((task) => task.status === "Выполнена"),
    [tasks]
  );
  const activeTasks = useMemo(
    () => tasks.filter((task) => task.status !== "Выполнена"),
    [tasks]
  );

  useLayoutEffect(() => {
    if (isLoadingTasks || !highlightedTaskId) {
      return;
    }
    const container = taskListRef.current;
    if (!container) {
      return;
    }
    const target = container.querySelector(
      `[data-task-id="${highlightedTaskId}"]`
    );
    if (!target) {
      return;
    }

    const scrollToTask = () => {
      const list = target.closest(
        ".project-details__task-list, .project-details__completed-list"
      );
      const scrollTargets = [];

      if (list && list.scrollHeight > list.clientHeight) {
        scrollTargets.push(list);
      }
      if (container.scrollHeight > container.clientHeight) {
        scrollTargets.push(container);
      }

      if (scrollTargets.length === 0) {
        target.scrollIntoView({ block: "center", behavior: "smooth" });
        return true;
      }

      scrollTargets.forEach((scrollTarget) => {
        const parentRect = scrollTarget.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();
        const offset = targetRect.top - parentRect.top - 16;
        scrollTarget.scrollTo({
          top: scrollTarget.scrollTop + offset,
          behavior: "smooth",
        });
      });
      return true;
    };

    const raf = requestAnimationFrame(scrollToTask);
    const timer = setTimeout(scrollToTask, 150);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer);
    };
  }, [isLoadingTasks, highlightedTaskId, showCompletedTasks, tasks]);

  useEffect(() => {
    if (!highlightedTaskId || isLoadingTasks) {
      return;
    }
    if (completedTasks.some((task) => task.id === highlightedTaskId)) {
      setShowCompletedTasks(true);
    }
  }, [completedTasks, highlightedTaskId, isLoadingTasks]);

  useEffect(() => {
    let isActive = true;
    setIsLoadingUsers(true);
    setUsersError("");
    apiFetch(`${API_BASE}/get_users`, { headers: authHeaders })
      .then((response) => {
        if (!response.ok) {
          throw new Error("failed");
        }
        return response.json();
      })
      .then((data) => {
        if (!isActive) {
          return;
        }
        const users = Array.isArray(data) ? data : [];
        setUsers(users);
        const directory = {};
        const fullNameDirectory = {};
        users.forEach((user) => {
          if (!user.username) {
            return;
          }
          const fullName =
            user.full_name ||
            [user.first_name, user.last_name].filter(Boolean).join(" ");
          const normalizedUsername = normalizeUsername(user.username);
          if (!normalizedUsername) {
            return;
          }
          directory[normalizedUsername] = {
            telegramId: user.telegram_id,
            fullName,
            firstName: user.first_name || "",
            lastName: user.last_name || "",
            username: user.username,
          };
          const normalizedFullName = normalizeFullName(fullName);
          if (normalizedFullName) {
            fullNameDirectory[normalizedFullName] = {
              fullName,
              firstName: user.first_name || "",
              lastName: user.last_name || "",
              username: user.username,
            };
          }
        });
        const profileId = profile?.telegram_id;
        const profileUsername = profile?.username?.replace(/^@/, "").toLowerCase();
        const matchedUser = users.find(
          (user) =>
            (profileId && user.telegram_id === profileId) ||
            (profileUsername &&
              user.username?.toLowerCase() === profileUsername)
        );
        if (matchedUser) {
          setEffectiveAccessLevel(
            normalizeAccessLevel(matchedUser.level_of_access || "", matchedUser.role || "")
          );
        }
        setUsersDirectory(directory);
        setUsersByFullName(fullNameDirectory);
        setIsLoadingUsers(false);
      })
      .catch(() => {
        if (!isActive) {
          return;
        }
        setUsers([]);
        setUsersError("Не удалось загрузить участников");
        setUsersDirectory({});
        setUsersByFullName({});
        setIsLoadingUsers(false);
      });

    return () => {
      isActive = false;
    };
  }, [authHeaders, profile]);

  const projectMembers = useMemo(() => {
    if (!project?.members) {
      return [];
    }
    return project.members;
  }, [project]);

  const members = useMemo(
    () =>
      projectMembers.map((member) => ({
        name: member.full_name || member.username || "Участник",
        role: normalizeProjectRole(member.role || ""),
        username: member.username || "",
      })),
    [projectMembers]
  );

  const userOptions = useMemo(
    () =>
      users
        .filter((user) => user.username)
        .map((user) => ({
          username: user.username,
          fullName:
            user.full_name ||
            [user.first_name, user.last_name].filter(Boolean).join(" "),
          telegramId: user.telegram_id,
        })),
    [users]
  );

  useEffect(() => {
    if (!projectMembers.length) {
      setMemberEdits({});
      return;
    }
    const nextEdits = {};
    projectMembers.forEach((member) => {
      const key = normalizeUsername(member.username || "");
      if (!key) {
        return;
      }
      const parsed = parseMemberRole(member.role || "");
      nextEdits[key] = {
        baseRole: parsed.baseRole,
        helper: parsed.helper,
      };
    });
    setMemberEdits(nextEdits);
  }, [projectMembers]);

  const isProjectMember = useMemo(() => {
    const profileUsername = profile?.username
      ? profile.username.replace(/^@/, "").toLowerCase()
      : "";
    if (!profileUsername || !project?.members) {
      return false;
    }
    return project.members.some(
      (member) =>
        (member.username || "").replace(/^@/, "").toLowerCase() ===
        profileUsername
    );
  }, [profile, project]);

  const memberRole = useMemo(() => {
    const profileUsername = profile?.username
      ? profile.username.replace(/^@/, "").toLowerCase()
      : "";
    if (!profileUsername || !project?.members) {
      return "";
    }
    const member = project.members.find(
      (entry) =>
        (entry.username || "").replace(/^@/, "").toLowerCase() ===
        profileUsername
    );
    return member?.role || "";
  }, [profile, project]);

  const isProjectHelper = useMemo(() => isLeader(memberRole), [memberRole]);

  const isGlobalAdmin = isAdmin(effectiveAccessLevel);
  const isGlobalModerator = isModerator(effectiveAccessLevel);

  const canManageProjectMembers = isGlobalAdmin || isGlobalModerator;
  const canManageTasks = isGlobalAdmin || isGlobalModerator || isProjectHelper;
	const projectRoles = parseRoles(memberRole);
	const isProjectLead = ["руководитель", "lead", "leader", "teamlead", "тимлид"].some((role) => projectRoles.includes(role));
	const canManageProjectStack = isGlobalAdmin || isGlobalModerator || isProjectLead;
  const canCreateTasks = canManageTasks || isProjectMember;
  const canViewTasks = isGlobalAdmin || isGlobalModerator || isProjectMember;

  useEffect(() => {
    let isActive = true;

    if (!canViewTasks) {
      setTasks([]);
      setTasksError("");
      setIsLoadingTasks(false);
      return () => {
        isActive = false;
      };
    }

    setIsLoadingTasks(true);
    setTasksError("");

    apiFetch(`${API_BASE}/tasks?id_project=${id}`, {
      headers: authHeaders,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("failed");
        }
        return response.json();
      })
      .then((data) => {
        if (!isActive) {
          return;
        }
        setTasks(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!isActive) {
          return;
        }
        setTasksError("Не удалось загрузить задачи");
      })
      .finally(() => {
        if (!isActive) {
          return;
        }
        setIsLoadingTasks(false);
      });

    return () => {
      isActive = false;
    };
  }, [authHeaders, id, canViewTasks]);

  const taskAuthor = useMemo(
    () =>
      formatDisplayName({
        fullName:
          profile?.full_name ||
          [profile?.first_name, profile?.last_name].filter(Boolean).join(" "),
        firstName: profile?.first_name,
        lastName: profile?.last_name,
        username: profile?.username,
      }),
    [profile]
  );

  const normalizedProfileUsername = normalizeUsername(profile?.username || "");
  const normalizedProfileFullName = normalizeFullName(
    profile?.full_name ||
      [profile?.first_name, profile?.last_name].filter(Boolean).join(" ")
  );

  const isTaskAssignee = (task) => {
    if (!task) {
      return false;
    }
    const users = Array.isArray(task.users) ? task.users : [];
    return users.some(
      (user) =>
        normalizeUsername(user.username || "") === normalizedProfileUsername ||
        (normalizedProfileFullName &&
          normalizeFullName(user.full_name || "") === normalizedProfileFullName)
    );
  };

  const isTaskAuthor = (task) => {
    const author = (task?.author || "").trim();
    if (!author) {
      return false;
    }
    const usernameMatch = author.match(/\(([^)]+)\)/);
    const authorUsername = normalizeUsername(
      usernameMatch ? usernameMatch[1] : author
    );
    if (normalizedProfileUsername && authorUsername === normalizedProfileUsername) {
      return true;
    }
    const authorName = normalizeFullName(
      author.replace(/\([^)]*\)/g, "").trim()
    );
    return Boolean(
      normalizedProfileFullName &&
        authorName &&
        authorName === normalizedProfileFullName
    );
  };

  const canSubmitCompletion = (task) => canManageTasks || isTaskAssignee(task);
  const canCloseTask = (task) => !canManageTasks && isTaskAuthor(task);

  const refreshProject = () =>
    apiFetch(`${API_BASE}/projects/${id}`, { headers: authHeaders })
      .then((response) => {
        if (!response.ok) {
          throw new Error("failed");
        }
        return response.json();
      })
      .then((data) => {
		setProject(data);
		setStackDraft(normalizeProjectStack(data?.stack));
	  });

	const handleSaveStack = () => {
	  if (!canManageProjectStack || isStackSaving) return;
	  setIsStackSaving(true);
	  setStackError("");
	  apiFetch(`${API_BASE}/projects/${id}/stack`, {
		method: "PUT",
		headers: { "Content-Type": "application/json", ...authHeaders },
		body: JSON.stringify(stackDraft),
	  })
		.then((response) => {
		  if (!response.ok) throw new Error("failed");
		  return refreshProject();
		})
		.then(() => setIsStackEditing(false))
		.catch(() => setStackError("Не удалось сохранить стек проекта"))
		.finally(() => setIsStackSaving(false));
	};

  const openStackModal = () => {
    setStackDraft(normalizeProjectStack(project?.stack));
    setStackError("");
    setIsStackEditing(true);
  };

  const closeStackModal = () => {
    if (isStackSaving) {
      return;
    }
    setStackDraft(normalizeProjectStack(project?.stack));
    setStackError("");
    setIsStackEditing(false);
  };

  const handleBannerUpload = (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = "";
    if (!file || isBannerSaving) {
      return;
    }
    if (!["image/png", "image/jpeg", "image/webp", "image/gif"].includes(file.type)) {
      setBannerError("Поддерживаются только PNG, JPEG, WebP и GIF");
      return;
    }
    if (file.size > MAX_BANNER_BYTES) {
      setBannerError("Файл больше 3 МБ");
      return;
    }
    setIsBannerSaving(true);
    setBannerError("");
    const form = new FormData();
    form.append("banner", file);
    apiFetch(`${API_BASE}/projects/${id}/banner`, {
      method: "PUT",
      headers: { ...authHeaders },
      body: form,
    })
      .then((response) => {
        if (!response.ok) throw new Error("failed");
        return refreshProject();
      })
      .catch(() => setBannerError("Не удалось загрузить баннер"))
      .finally(() => setIsBannerSaving(false));
  };

  const handleBannerRemove = () => {
    if (!project?.banner_url || isBannerSaving) {
      return;
    }
    setIsBannerSaving(true);
    setBannerError("");
    apiFetch(`${API_BASE}/projects/${id}/banner`, {
      method: "DELETE",
      headers: { ...authHeaders },
    })
      .then((response) => {
        if (!response.ok) throw new Error("failed");
        return refreshProject();
      })
      .catch(() => setBannerError("Не удалось удалить баннер"))
      .finally(() => setIsBannerSaving(false));
  };

  const handleMemberFormChange = (event) => {
    const { name, value, type, checked } = event.target;
    setMemberForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleMemberEditChange = (username, field, value) => {
    setMemberEdits((prev) => {
      const current = prev[username] || {
        baseRole: "Участник",
        helper: false,
      };
      return {
        ...prev,
        [username]: {
          ...current,
          [field]: value,
        },
      };
    });
  };

  const handleAddMember = (event) => {
    event.preventDefault();
    if (!canManageProjectMembers) {
      return;
    }
    setMembersActionError("");

    const normalized = normalizeUsername(memberForm.username);
    if (!normalized) {
      setMembersActionError("Введите username участника");
      return;
    }

    const roleValue = buildRoleString(memberForm.baseRole, memberForm.helper);
    const userInfo = userOptions.find(
      (user) => normalizeUsername(user.username || "") === normalized
    );

    apiFetch(`${API_BASE}/projects/${id}/members`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders,
      },
      body: JSON.stringify({
        username: normalized,
        full_name: userInfo?.fullName || "",
        role: roleValue,
        telegram_id: userInfo?.telegramId || "",
      }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("failed");
        }
        return refreshProject();
      })
      .then(() => {
        setMemberForm({
          username: "",
          baseRole: "Участник",
          helper: false,
        });
      })
      .catch(() => setMembersActionError("Не удалось добавить участника"));
  };

  const handleUpdateMemberRole = (username) => {
    if (!canManageProjectMembers) {
      return;
    }
    setMembersActionError("");
    const edit = memberEdits[username];
    if (!edit) {
      return;
    }
    const roleValue = buildRoleString(edit.baseRole, edit.helper);

    apiFetch(`${API_BASE}/projects/${id}/members/${username}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders,
      },
      body: JSON.stringify({ role: roleValue }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("failed");
        }
        return refreshProject();
      })
      .catch(() => setMembersActionError("Не удалось обновить роль"));
  };

  const handleRemoveMember = (username) => {
    if (!canManageProjectMembers) {
      return;
    }
    setMembersActionError("");

    apiFetch(`${API_BASE}/projects/${id}/members/${username}`, {
      method: "DELETE",
      headers: authHeaders,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("failed");
        }
        return refreshProject();
      })
      .catch(() => setMembersActionError("Не удалось удалить участника"));
  };

  const formatTaskPerson = (rawValue) => {
    if (!rawValue) {
      return "";
    }

    const usernameMatch = rawValue.match(/\(([^)]+)\)/);
    const usernameFromParens = usernameMatch
      ? normalizeUsername(usernameMatch[1])
      : "";
    const trimmedName = rawValue.replace(/\([^)]*\)/g, "").trim();
    const normalizedFullName = normalizeFullName(trimmedName || rawValue);
    const normalizedUsername = usernameFromParens || normalizeUsername(rawValue);

    const byUsername = usersDirectory[normalizedUsername];
    const byFullName = usersByFullName[normalizedFullName];
    const user = byUsername || byFullName;
    const fullName = user?.fullName || (trimmedName || (byFullName ? rawValue : ""));
    const username = user?.username || (usernameFromParens || (byUsername ? rawValue : ""));
    const formatted = formatDisplayName({
      fullName,
      firstName: user?.firstName,
      lastName: user?.lastName,
      username,
    });

    return formatted || rawValue;
  };

  const projectMemberDirectory = useMemo(() => {
    const directory = {};
    projectMembers.forEach((member) => {
      const key = normalizeUsername(member.username || "");
      if (key) {
        directory[key] = member;
      }
    });
    return directory;
  }, [projectMembers]);

  const projectMemberUsernames = useMemo(
    () =>
      projectMembers
        .map((member) => normalizeUsername(member.username || ""))
        .filter(Boolean),
    [projectMembers]
  );

  const formatTaskUsers = (taskUsers, legacyUser) => {
    const list = Array.isArray(taskUsers) ? taskUsers : [];
    if (list.length === 0) {
      return legacyUser ? formatTaskPerson(legacyUser) : "";
    }

    if (projectMemberUsernames.length > 0) {
      const assigned = list
        .map((user) => normalizeUsername(user.username || ""))
        .filter(Boolean);
      if (
        assigned.length > 0 &&
        projectMemberUsernames.every((username) => assigned.includes(username))
      ) {
        return "Вся команда";
      }
    }

    const labels = list
      .map((user) => {
        const rawValue = user.username || user.full_name || "";
        const formatted = formatTaskPerson(rawValue);
        return formatted || rawValue;
      })
      .filter(Boolean);

    return labels.join(", ");
  };

  const buildUsersPayload = (selectedUsernames, assignAll) => {
    const source = assignAll ? projectMembers : selectedUsernames;
    const list = Array.isArray(source) ? source : [];
    const payload = [];

    list.forEach((entry) => {
      if (!entry) {
        return;
      }
      if (typeof entry === "string") {
        const key = normalizeUsername(entry);
        if (!key) {
          return;
        }
        const member = projectMemberDirectory[key];
        const fallback = usersDirectory[key];
        payload.push({
          username: member?.username || fallback?.username || key,
          full_name: member?.full_name || fallback?.fullName || "",
          role: member?.role || "",
          telegram_id: member?.telegram_id || fallback?.telegramId || "",
        });
        return;
      }
      const key = normalizeUsername(entry.username || "");
      if (!key) {
        return;
      }
      payload.push({
        username: entry.username || key,
        full_name: entry.full_name || "",
        role: entry.role || "",
        telegram_id: entry.telegram_id || "",
      });
    });

    return payload;
  };

  const handleTaskChange = (event) => {
    const { name, value } = event.target;
    setTaskForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTeamToggle = (checked) => {
    setTaskForm((prev) => ({
      ...prev,
      assignAll: checked,
      users: checked ? projectMemberUsernames : [],
    }));
  };

  const handleUserToggle = (username) => {
    setTaskForm((prev) => {
      const nextUsers = prev.users.includes(username)
        ? prev.users.filter((entry) => entry !== username)
        : [...prev.users, username];
      const assignAll =
        projectMemberUsernames.length > 0 &&
        projectMemberUsernames.every((entry) => nextUsers.includes(entry));
      return {
        ...prev,
        users: nextUsers,
        assignAll,
      };
    });
  };

  const handleTaskSubmit = (event) => {
    event.preventDefault();
    const trimmedTitle = taskForm.title.trim();
    if (!trimmedTitle) {
      setTaskFormError("Добавьте название задачи");
      return;
    }

    setIsSavingTask(true);
    setTaskFormError("");

    const usersPayload = buildUsersPayload(
      taskForm.users,
      taskForm.assignAll
    );
    if (usersPayload.length === 0) {
      setTaskFormError("Выберите ответственных");
      setIsSavingTask(false);
      return;
    }

    const payload = {
      title: trimmedTitle,
      description: taskForm.description.trim(),
      deadline: taskForm.deadline,
      status: "Новая",
      author: taskAuthor,
      id_project: Number(id),
      users: usersPayload,
    };

    apiFetch(`${API_BASE}/tasks`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders,
      },
      body: JSON.stringify(payload),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("failed");
        }
        return response.json();
      })
      .then((data) => {
        setTasks((prev) => [data, ...prev]);
        setTaskForm({
          title: "",
          description: "",
          deadline: "",
          users: [],
          assignAll: false,
        });
        setShowTaskForm(false);
      })
      .catch(() => {
        setTaskFormError("Не удалось добавить задачу");
      })
      .finally(() => {
        setIsSavingTask(false);
      });
  };

  const handleCompletionSubmit = (taskId) => {
    const message = (completionNotes[taskId] || "").trim();
    apiFetch(`${API_BASE}/tasks/${taskId}/complete`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders,
      },
      body: JSON.stringify({ message }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("failed");
        }
        return response.text();
      })
      .then(() => {
        setTasks((prev) =>
          prev.map((task) =>
            task.id === taskId
              ? {
                  ...task,
                  status: "На проверке",
                  completion_message: message,
                }
              : task
          )
        );
        setCompletionNotes((prev) => ({ ...prev, [taskId]: "" }));
      })
      .catch(() => {
        setTasksError("Не удалось отправить отчет по задаче");
      });
  };

  const handleCloseTask = (taskId) => {
    apiFetch(`${API_BASE}/tasks/${taskId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders,
      },
      body: JSON.stringify({ status: "Выполнена" }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("failed");
        }
        return response.text();
      })
      .then(() => {
        setTasks((prev) =>
          prev.map((task) =>
            task.id === taskId ? { ...task, status: "Выполнена" } : task
          )
        );
      })
      .catch(() => {
        setTasksError("Не удалось закрыть задачу");
      });
  };

  return (
    <section className="project-details">
      <div className="project-details__layout">
        <div className="project-details__content">
          <header className="project-details__header">
            <Link
              className="project-details__back"
              to={backPath}
              aria-label="Назад"
            >
              ←
            </Link>
            <div className="project-details__title">
              <p className="project-details__label">Просмотр проекта</p>
              <h2 className="project-details__name">
                {project?.title || "Проект"}
              </h2>
              <p className="project-details__description">
                {project?.description || "Описание пока не добавлено."}
              </p>
            </div>
            {canManageTasks && (
              <Link
                className="project-details__action"
                to={`/projects/${id}/manage`}
              >
                Управление
              </Link>
            )}
          </header>

          {isLoading && (
            <div className="project-details__placeholder">
              Загрузка данных проекта...
            </div>
          )}
          {!isLoading && projectError && (
            <div className="project-details__placeholder">{projectError}</div>
          )}

          {!isLoading && !projectError && project && (
            <section className="project-details__stack-section">
              <div className="project-details__stack-header">
                <h3>Стек проекта</h3>
                {canManageProjectStack && (
                  <button type="button" onClick={openStackModal}>
                    Настроить
                  </button>
                )}
              </div>
              <ProjectStackEditor
                value={project.stack}
                editable={false}
                compact
              />
            </section>
          )}

          {!isLoading && !projectError && project && (
            <section className="project-details__banner-section">
              <div className="project-details__banner-header">
                <h3>Баннер проекта</h3>
                {canManageProjectStack && (
                  <div className="project-details__banner-actions">
                    <input
                      id="project-banner-file"
                      className="project-details__banner-file"
                      type="file"
                      accept={BANNER_ACCEPT}
                      onChange={handleBannerUpload}
                      disabled={isBannerSaving}
                    />
                    <label className="project-details__banner-button" htmlFor="project-banner-file">
                      {isBannerSaving ? "Сохраняем..." : "Загрузить баннер"}
                    </label>
                    {project.banner_url && (
                      <button
                        type="button"
                        className="project-details__banner-remove"
                        onClick={handleBannerRemove}
                        disabled={isBannerSaving}
                      >
                        Удалить
                      </button>
                    )}
                  </div>
                )}
              </div>
              <ProjectBannerImage
                bannerUrl={project.banner_url}
                title={project.title}
                className="project-details__banner"
              />
              {!project.banner_url && (
                <p className="project-details__banner-empty">
                  Баннер ещё не загружен.
                </p>
              )}
              {bannerError && (
                <p className="project-details__banner-error">{bannerError}</p>
              )}
            </section>
          )}

          {!isLoading && !projectError && project && (
            <section className="project-details__tasks" ref={taskListRef}>
              <div className="project-details__tasks-header">
                <h3>Задачи</h3>
                <span className="project-details__tasks-count">
                  {activeTasks.length}
                </span>
                {canCreateTasks && (
                  <button
                    className="project-details__tasks-toggle"
                    type="button"
                    onClick={() => setShowTaskForm((prev) => !prev)}
                    aria-expanded={showTaskForm}
                  >
                    {showTaskForm ? "—" : "+"}
                  </button>
                )}
              </div>
              {!canViewTasks ? (
                <div className="project-details__task-list">
                  <div className="project-details__task">
                    <p className="project-details__task-title">Задачи скрыты</p>
                    <p className="project-details__task-subtitle">
                      Доступ к задачам есть только у участников проекта.
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="project-details__task-list">
                    {isLoadingTasks && (
                      <div className="project-details__task">
                        <p className="project-details__task-title">
                          Загрузка задач...
                        </p>
                      </div>
                    )}
                    {!isLoadingTasks && tasksError && (
                      <div className="project-details__task">
                        <p className="project-details__task-title">
                          {tasksError}
                        </p>
                      </div>
                    )}
                    {!isLoadingTasks &&
                      !tasksError &&
                      activeTasks.length === 0 &&
                      completedTasks.length === 0 && (
                        <div className="project-details__task">
                          <p className="project-details__task-title">
                            Задач пока нет
                          </p>
                          <p className="project-details__task-subtitle">
                            Здесь появятся реальные задачи проекта.
                          </p>
                        </div>
                      )}
                    {!isLoadingTasks &&
                      !tasksError &&
                      activeTasks.length === 0 &&
                      completedTasks.length > 0 && (
                        <div className="project-details__task">
                          <p className="project-details__task-title">
                            Активных задач пока нет
                          </p>
                          <p className="project-details__task-subtitle">
                            Выполненные задачи доступны ниже.
                          </p>
                        </div>
                      )}
                    {!isLoadingTasks &&
                      !tasksError &&
                      activeTasks.map((task) => (
                        <article
                          className={`project-details__task-card${
                            highlightedTaskId === task.id
                              ? " project-details__task-card--highlight"
                              : ""
                          }`}
                          key={`${task.id}-${task.title}`}
                          data-task-id={task.id}
                        >
                          <div className="project-details__task-main">
                            <p className="project-details__task-title">
                              {task.title || "Без названия"}
                            </p>
                            {task.description && (
                              <p className="project-details__task-subtitle">
                                {task.description}
                              </p>
                            )}
                          </div>
                          <div className="project-details__task-meta">
                            <span>{task.status || "Без статуса"}</span>
                            <span>Срок: {formatDeadline(task.deadline)}</span>
                            {formatTaskUsers(task.users, task.user) && (
                              <span>
                                Исп.: {formatTaskUsers(task.users, task.user)}
                              </span>
                            )}
                            {task.author && (
                              <span>Автор: {formatTaskPerson(task.author)}</span>
                            )}
                          </div>
                          {task.completion_message && (
                            <div className="project-details__task-note">
                              <strong>Отчет:</strong>{" "}
                              {task.completion_message}
                            </div>
                          )}
                          {task.review_message && (
                            <div className="project-details__task-note">
                              <strong>Ответ модератора:</strong>{" "}
                              {task.review_message}
                            </div>
                          )}
                          {task.status !== "На проверке" &&
                            task.status !== "Выполнена" &&
                            canSubmitCompletion(task) && (
                              <div className="project-details__task-completion">
                                <textarea
                                  placeholder="Сообщение о выполненной работе"
                                  rows={2}
                                  value={completionNotes[task.id] || ""}
                                  onChange={(event) =>
                                    setCompletionNotes((prev) => ({
                                      ...prev,
                                      [task.id]: event.target.value,
                                    }))
                                  }
                                />
                                <button
                                  type="button"
                                  onClick={() => handleCompletionSubmit(task.id)}
                                >
                                  Отправить на проверку
                                </button>
                              </div>
                            )}
                          {canCloseTask(task) &&
                            task.status !== "Выполнена" &&
                            task.status !== "На проверке" && (
                              <div className="project-details__task-close">
                                <button
                                  type="button"
                                  onClick={() => handleCloseTask(task.id)}
                                >
                                  Закрыть задачу
                                </button>
                              </div>
                            )}
                        </article>
                      ))}
                  </div>
                  {showTaskForm && canCreateTasks && (
                    <form
                      className="project-details__task-form"
                      onSubmit={handleTaskSubmit}
                    >
                      <h4>Добавить задачу</h4>
                      <div className="project-details__form-grid">
                        <label className="project-details__field">
                          Название
                          <input
                            name="title"
                            value={taskForm.title}
                            onChange={handleTaskChange}
                            placeholder="Кратко о задаче"
                            required
                          />
                        </label>
                        <div className="project-details__field">
                          <span>Ответственные</span>
                          <div className="project-details__users">
                            <label className="project-details__users-toggle">
                              <input
                                type="checkbox"
                                checked={taskForm.assignAll}
                                onChange={(event) =>
                                  handleTeamToggle(event.target.checked)
                                }
                                disabled={projectMemberUsernames.length === 0}
                              />
                              <span>Вся команда</span>
                            </label>
                            <div className="project-details__users-list">
                              {members.length === 0 && (
                                <span className="project-details__users-empty">
                                  Нет участников
                                </span>
                              )}
                              {members.map((member) => {
                                const normalized = normalizeUsername(
                                  member.username || ""
                                );
                                if (!normalized) {
                                  return null;
                                }
                                return (
                                  <label
                                    className="project-details__users-item"
                                    key={member.username || member.name}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={taskForm.users.includes(
                                        normalized
                                      )}
                                      onChange={() =>
                                        handleUserToggle(normalized)
                                      }
                                      disabled={taskForm.assignAll}
                                    />
                                    <span>{member.name}</span>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                        <label className="project-details__field">
                          Дедлайн
                          <input
                            type="date"
                            name="deadline"
                            value={taskForm.deadline}
                            onChange={handleTaskChange}
                          />
                        </label>
                        <label className="project-details__field project-details__field--full">
                          Описание
                          <textarea
                            name="description"
                            value={taskForm.description}
                            onChange={handleTaskChange}
                            placeholder="Что нужно сделать"
                            rows={3}
                          />
                        </label>
                      </div>
                      {taskFormError && (
                        <p className="project-details__form-error">
                          {taskFormError}
                        </p>
                      )}
                      <div className="project-details__form-actions">
                        <button
                          className="project-details__submit"
                          type="submit"
                          disabled={isSavingTask}
                        >
                          {isSavingTask ? "Сохранение..." : "Добавить"}
                        </button>
                      </div>
                    </form>
                  )}
                  <div className="project-details__completed">
                    <button
                      className="project-details__completed-toggle"
                      type="button"
                      onClick={() => setShowCompletedTasks((prev) => !prev)}
                      aria-expanded={showCompletedTasks}
                    >
                      <span>Выполненные задачи</span>
                      <span className="project-details__completed-count">
                        {completedTasks.length}
                      </span>
                    </button>
                    {showCompletedTasks && (
                      <div className="project-details__completed-list">
                        {completedTasks.length === 0 && (
                          <div className="project-details__task">
                            <p className="project-details__task-title">
                              Выполненных задач пока нет
                            </p>
                          </div>
                        )}
                        {completedTasks.map((task) => (
                          <article
                            className={`project-details__task-card${
                              highlightedTaskId === task.id
                                ? " project-details__task-card--highlight"
                                : ""
                            }`}
                            key={`${task.id}-${task.title}-completed`}
                            data-task-id={task.id}
                          >
                            <div className="project-details__task-main">
                              <p className="project-details__task-title">
                                {task.title || "Без названия"}
                              </p>
                              {task.description && (
                                <p className="project-details__task-subtitle">
                                  {task.description}
                                </p>
                              )}
                            </div>
                            <div className="project-details__task-meta">
                              <span>{task.status || "Без статуса"}</span>
                              <span>Срок: {formatDeadline(task.deadline)}</span>
                              {formatTaskUsers(task.users, task.user) && (
                                <span>
                                  Исп.: {formatTaskUsers(task.users, task.user)}
                                </span>
                              )}
                              {task.author && (
                                <span>
                                  Автор: {formatTaskPerson(task.author)}
                                </span>
                              )}
                            </div>
                            {task.completion_message && (
                              <div className="project-details__task-note">
                                <strong>Отчет:</strong>{" "}
                                {task.completion_message}
                              </div>
                            )}
                            {task.review_message && (
                              <div className="project-details__task-note">
                                <strong>Ответ модератора:</strong>{" "}
                                {task.review_message}
                              </div>
                            )}
                          </article>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </section>
          )}
        </div>

        <aside className="project-details__side">
          <div className="project-details__status">
            <p>Статус: {project?.status || "—"}</p>
          </div>
          <div className="project-details__members">
            <div className="project-details__members-header">
              <h3>Участники</h3>
              {canManageProjectMembers && (
                <button
                  type="button"
                  className={`project-details__members-toggle${
                    isMembersEditing ? " is-active" : ""
                  }`}
                  onClick={() => setIsMembersEditing((prev) => !prev)}
                  aria-expanded={isMembersEditing}
                >
                  {isMembersEditing ? "Готово" : "Управлять"}
                </button>
              )}
            </div>
            {isMembersEditing && membersActionError && (
              <p className="project-details__members-error">
                {membersActionError}
              </p>
            )}
            <div className="project-details__members-list">
              {isLoading && (
                <div className="project-details__member">
                  Загрузка участников...
                </div>
              )}
              {!isLoading && projectError && (
                <div className="project-details__member">{projectError}</div>
              )}
              {!isLoading && !projectError && projectMembers.length === 0 && (
                <div className="project-details__member">Нет участников</div>
              )}
              {!isLoading &&
                !projectError &&
                projectMembers.map((member) => {
                  const key = normalizeUsername(member.username || "");
                  const displayName =
                    member.full_name || member.username || "Участник";
                  const displayRole = normalizeProjectRole(member.role || "");
                  const edit = memberEdits[key] || {
                    baseRole: "Участник",
                    helper: false,
                  };
                  return (
                    <div
                      className="project-details__member"
                      key={member.username || member.full_name || displayName}
                    >
                      <div className="project-details__member-info">
                        <span>{displayName}</span>
                        {displayRole && <em>{displayRole}</em>}
                      </div>
                      {isMembersEditing && canManageProjectMembers && key && (
                        <>
                          <div className="project-details__member-controls">
                            <select
                              value={edit.baseRole}
                              onChange={(event) =>
                                handleMemberEditChange(
                                  key,
                                  "baseRole",
                                  event.target.value
                                )
                              }
                            >
                              {BASE_ROLES.map((roleOption) => (
                                <option key={roleOption} value={roleOption}>
                                  {roleOption}
                                </option>
                              ))}
                            </select>
                            <label className="project-details__member-toggle">
                              <input
                                type="checkbox"
                                checked={edit.helper}
                                onChange={(event) =>
                                  handleMemberEditChange(
                                    key,
                                    "helper",
                                    event.target.checked
                                  )
                                }
                              />
                              <span>Хелпер</span>
                            </label>
                          </div>
                          <div className="project-details__member-actions">
                            <button
                              type="button"
                              onClick={() => handleUpdateMemberRole(key)}
                            >
                              Сохранить
                            </button>
                            <button
                              type="button"
                              className="is-danger"
                              onClick={() => handleRemoveMember(key)}
                            >
                              Удалить
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
            </div>
            {canManageProjectMembers && isMembersEditing && (
              <form
                className="project-details__member-form"
                onSubmit={handleAddMember}
              >
                <label>
                  Username
                  <input
                    list="project-members-users"
                    name="username"
                    value={memberForm.username}
                    onChange={handleMemberFormChange}
                    placeholder="@username"
                  />
                </label>
                <label>
                  Роль
                  <select
                    name="baseRole"
                    value={memberForm.baseRole}
                    onChange={handleMemberFormChange}
                  >
                    {BASE_ROLES.map((roleOption) => (
                      <option key={roleOption} value={roleOption}>
                        {roleOption}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="project-details__member-toggle">
                  <input
                    type="checkbox"
                    name="helper"
                    checked={memberForm.helper}
                    onChange={handleMemberFormChange}
                  />
                  <span>Хелпер</span>
                </label>
                <button type="submit">Добавить</button>
                {usersError && (
                  <span className="project-details__members-hint">
                    {usersError}
                  </span>
                )}
                {isLoadingUsers && (
                  <span className="project-details__members-hint">
                    Загрузка списка участников...
                  </span>
                )}
              </form>
            )}
            <datalist id="project-members-users">
              {userOptions.map((user) => (
                <option key={user.username} value={user.username}>
                  {user.fullName || user.username}
                </option>
              ))}
            </datalist>
          </div>
        </aside>
      </div>
      {isStackEditing &&
        createPortal(
          <div
            className="project-stack-modal__overlay"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                closeStackModal();
              }
            }}
          >
            <section
              className="project-stack-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="project-stack-modal-title"
            >
              <header className="project-stack-modal__header">
                <div>
                  <p className="project-details__label">Редактирование проекта</p>
                  <h2 id="project-stack-modal-title">Стек: {project?.title}</h2>
                  <p>
                    Выберите технологии по контурам или добавьте собственный
                    вариант.
                  </p>
                </div>
                <button
                  className="project-stack-modal__close"
                  type="button"
                  onClick={closeStackModal}
                  disabled={isStackSaving}
                  aria-label="Закрыть"
                >
                  ×
                </button>
              </header>
              <div className="project-stack-modal__body">
                <ProjectStackEditor
                  value={stackDraft}
                  onChange={setStackDraft}
                  editable
                />
              </div>
              <footer className="project-stack-modal__footer">
                <div>
                  {stackError && (
                    <p className="project-details__stack-error">{stackError}</p>
                  )}
                </div>
                <div className="project-stack-modal__actions">
                  <button
                    className="project-stack-modal__cancel"
                    type="button"
                    onClick={closeStackModal}
                    disabled={isStackSaving}
                  >
                    Отмена
                  </button>
                  <button
                    className="project-stack-modal__save"
                    type="button"
                    onClick={handleSaveStack}
                    disabled={isStackSaving}
                  >
                    {isStackSaving ? "Сохранение..." : "Сохранить стек"}
                  </button>
                </div>
              </footer>
            </section>
          </div>,
          document.body
        )}
    </section>
  );
}
